import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, Phone, User, LogOut } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "New Stock", to: "/shop?new=1" },
  { label: "Contact", to: "/#contact" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user, openAuth, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            className="font-display font-black text-lg md:text-2xl tracking-[0.2em] text-white"
            data-testid="nav-logo"
          >
            THRIFT<span className="text-neutral-500">HOUSE</span>
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className="font-mono-th text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-white hover-underline"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            {user && user.role ? (
              <div className="hidden md:flex items-center gap-3">
                <span className="font-mono-th text-[11px] uppercase tracking-[0.15em] text-neutral-300" data-testid="nav-user-name">
                  {user.name?.split(" ")[0] || "Account"}
                </span>
                <button onClick={logout} className="text-neutral-400 hover:text-white" data-testid="nav-logout-button" aria-label="Log out">
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => openAuth({ mode: "login" })}
                className="hidden md:flex items-center gap-2 text-neutral-200 hover:text-white transition-colors font-mono-th text-[11px] uppercase tracking-[0.15em]"
                data-testid="nav-signin-button"
              >
                <User className="w-4 h-4" strokeWidth={1.5} /> Sign In
              </button>
            )}
            <button
              onClick={() => setOpen(true)}
              className="relative text-neutral-200 hover:text-white transition-colors"
              data-testid="nav-cart-button"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-white text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
                  data-testid="nav-cart-count"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden text-neutral-200"
              onClick={() => setMobileOpen(true)}
              data-testid="nav-mobile-menu-button"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] glass md:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-white/10">
              <span className="font-display font-black tracking-[0.2em]">MENU</span>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex flex-col p-8 gap-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="font-display text-3xl font-bold uppercase"
                    data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="mt-auto p-8 border-t border-white/10 space-y-4">
              {user && user.role ? (
                <button
                  onClick={() => { logout(); setMobileOpen(false); }}
                  className="flex items-center gap-3 text-neutral-300 font-mono-th text-sm uppercase tracking-[0.15em]"
                  data-testid="mobile-logout-button"
                >
                  <LogOut className="w-4 h-4" /> Log out ({user.name?.split(" ")[0]})
                </button>
              ) : (
                <button
                  onClick={() => { openAuth({ mode: "login" }); setMobileOpen(false); }}
                  className="flex items-center gap-3 text-neutral-300 font-mono-th text-sm uppercase tracking-[0.15em]"
                  data-testid="mobile-signin-button"
                >
                  <User className="w-4 h-4" /> Sign In
                </button>
              )}
              <a href="tel:9570216659" className="flex items-center gap-3 text-neutral-300 font-mono-th text-sm">
                <Phone className="w-4 h-4" /> 9570216659
              </a>
              <a href="tel:9508859162" className="flex items-center gap-3 text-neutral-300 font-mono-th text-sm">
                <Phone className="w-4 h-4" /> 9508859162
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
